import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Send, X, Loader2, RotateCcw } from "lucide-react";
import { API, getChat, clearChat } from "../../lib/api";
import { visitorId, streamPost } from "../../lib/stream";
import { M } from "../../data/site";

const GREETING = { role: "assistant", text: "Hi, I’m Honesti! Ask me about tonight’s dinner, picky eaters, or what HonestChoice AI is building. I’m a live AI preview — the full product isn’t launched yet." };
const SUGGESTIONS = ["What can you help my family with?", "Quick dinner idea for picky kids?", "How do I join the waitlist?"];

const Bubble = ({ m }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <p className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "rounded-br-sm bg-blue-600 text-white" : "rounded-bl-sm bg-white/[0.07] text-slate-100"}`}>{m.text ? m.text.replace(/\*\*/g, "") : "…"}</p>
  </div>
);

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    getChat(visitorId()).then(setMsgs).catch(() => {});
  }, [open]);
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs, open]);

  const send = async (text) => {
    const message = text.trim().slice(0, 600);
    if (!message || busy) return;
    setDraft("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    try {
      await streamPost(`${API}/ai/chat`, { visitor_id: visitorId(), message }, (ev) => {
        if (ev.remaining !== undefined) setRemaining(ev.remaining);
        if (ev.t) setMsgs((m) => [...m.slice(0, -1), { role: "assistant", text: m[m.length - 1].text + ev.t }]);
      });
    } catch (e) {
      setMsgs((m) => [...m.slice(0, -1), { role: "assistant", text: e.message }]);
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => { await clearChat(visitorId()).catch(() => {}); setMsgs([]); };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div key="chat" data-testid="chat-panel" initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.96 }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            data-lenis-prevent className="glass fixed bottom-24 right-4 z-[70] flex h-[min(600px,calc(100svh-8rem))] w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-3xl sm:right-6 sm:w-[380px]">
            <div className="flex items-center gap-3 border-b border-cyan-400/20 px-4 py-3">
              <img src={M("honesti.jpg")} alt="Honesti" className="h-10 w-10 rounded-full object-cover object-top ring-2 ring-cyan-300/60" />
              <div className="flex-1">
                <p className="font-display font-semibold text-white">Ask Honesti</p>
                <p className="flex items-center gap-1.5 text-xs text-emerald-200"><span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />Live AI preview</p>
              </div>
              <button onClick={reset} data-testid="chat-reset-btn" aria-label="Clear conversation" className="rounded-full p-2 text-slate-300 hover:bg-white/10 hover:text-white"><RotateCcw className="h-4 w-4" /></button>
              <button onClick={() => setOpen(false)} data-testid="chat-close-btn" aria-label="Close chat" className="rounded-full p-2 text-slate-300 hover:bg-white/10 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <div ref={listRef} data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              <Bubble m={GREETING} />
              {msgs.map((m, i) => <Bubble key={i} m={m} />)}
              {!msgs.length && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGESTIONS.map((s) => <button key={s} onClick={() => send(s)} data-testid="chat-suggestion" className="rounded-full border border-cyan-400/30 px-3 py-1.5 text-xs text-cyan-100 transition-colors hover:border-cyan-300 hover:bg-cyan-400/10">{s}</button>)}
                </div>
              )}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(draft); }} className="border-t border-cyan-400/20 p-3">
              <div className="flex gap-2">
                <input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={600} placeholder="Ask Honesti anything…" data-testid="chat-input" className="flex-1 rounded-full border border-cyan-400/25 bg-deep/80 px-4 py-2.5 text-sm text-white placeholder:text-slate-400 focus:border-cyan-300 focus:outline-none" />
                <button disabled={busy || !draft.trim()} data-testid="chat-send-btn" aria-label="Send" className="grid h-10 w-10 place-items-center rounded-full bg-cyan-400 text-ink transition-opacity disabled:opacity-40">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
              </div>
              <p className="mt-2 text-center text-[0.7rem] text-slate-400">Powered by ChatGPT · saved to this browser{remaining !== null ? ` · ${remaining} messages left today` : ""}</p>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.button onClick={() => setOpen((o) => !o)} data-testid="chat-toggle-btn" aria-label={open ? "Close chat" : "Ask Honesti"} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
        className="fixed bottom-5 right-4 z-[70] flex items-center gap-3 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 py-2 pl-2 pr-5 text-sm font-semibold text-white shadow-[0_0_30px_rgba(34,211,238,0.5)] sm:right-6">
        <span className="relative grid h-10 w-10 place-items-center overflow-hidden rounded-full ring-2 ring-white/70">
          {open ? <X className="h-5 w-5" /> : <img src={M("honesti.jpg")} alt="" className="h-full w-full object-cover object-top" />}
        </span>
        {open ? "Close" : <span className="flex items-center gap-1.5"><MessageCircle className="h-4 w-4" />Ask Honesti</span>}
      </motion.button>
    </>
  );
};
